import { CheckCircleIcon, ExclamationCircleIcon, XCircleIcon } from "@heroicons/react/24/solid";
import { Button } from "components/base/Button";
import { Card } from "components/base/Card";
import { Text } from "components/base/Text";
import { Title } from "components/base/Title";
import { parseToCurrency } from "context/panier-context";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getPanierPayer, PanierPayer } from "services/apiPanier";

type STATUS_TYPE = "succeeded" | "processing" | "requires_payment_method" | "default";


export default function ComletePaiement() {
	const navigate = useNavigate();

	const [status, setStatus] = useState<STATUS_TYPE>("default");
	const [paymentIntentId, setPaymentIntentId] = useState<string>("");
	const [panierPayer, setPanierPayer] = useState<PanierPayer | null>(null);

	useEffect(() => {
		setStatus(new URLSearchParams(window.location.search).get("redirect_status") as STATUS_TYPE || "default");
		setPaymentIntentId(new URLSearchParams(window.location.search).get("payment_intent") as string || "");

		const fetchLivraison = async () => {
			if (status === "succeeded") {
				try {
					const response = await getPanierPayer(paymentIntentId);
					setPanierPayer(response);
				} catch (error) {
					console.error(error);
				}
			}
		}

		fetchLivraison();
	}, [status]);

	const statusColor = (status: STATUS_TYPE) => {
		switch (status) {
			case "succeeded":
				return "text-success";
			case "processing":
				return "text-info";
			case "requires_payment_method":
				return "text-orange-700";
			default:
				return "text-alert";
		}
	}



	return (
		<div
			className="min-h-container flex flex-col items-center justify-center text-center -my-4 py-4"
			style={{
				backgroundImage: 'url(/bg-error.JPG)',
				backgroundRepeat: "no-repeat",
				backgroundSize: "cover"
			}}
		>
			<div className='absolute inset-0 bg-gray-600 opacity-30' />

			<Card className="p-8 flex flex-col items-center justify-center gap-8 z-10 shadow-sm shadow-primary-100 max-w-lg">
				{status === "succeeded" ? (
					<CheckCircleIcon className={`w-20 h-20 ${statusColor(status)} rounded-full bg-success-100`} />
				) : status === "processing" ? (
					<ExclamationCircleIcon className={`w-20 h-20 ${statusColor(status)} rounded-full bg-info-100`} />
				) : status === "requires_payment_method" ? (
					<ExclamationCircleIcon className={`w-20 h-20 ${statusColor(status)} rounded-full bg-orange-500-100`} />
				) : (
					<XCircleIcon className={`w-20 h-20 ${statusColor(status)} rounded-full bg-alert-100`} />
				)}
				{/* Logo et nom du site */}
				<div className="flex flex-col items-center gap-2">
					<Title type="h1" className={`text-4xl ${statusColor(status)}`}>
						{status === "succeeded" ? "Paiement effectué"
							: status === "processing" ? "Paiement en cours"
								: status === "requires_payment_method" ? "Paiement en attente"
									: "Paiement refusé"
						}
					</Title>

					<Title type="h6" className="text-gray-800">
						{status === "succeeded" ? "Merci pour votre commande."
							: status === "processing" ? "Votre paiement est en cours de traitement... Merci de patienter."
								: status === "requires_payment_method" ? "Votre paiement est en attente de paiement d'un autre moyen de paiement."
									: "Votre paiement a été refusé. Veuillez réessayer plus tard."}
					</Title>
				</div>

				{status === "succeeded" && panierPayer && (
					<div className="w-full text-left">
						<Text className="text-gray-600 font-semibold mb-2">Récapitulatif de votre commande</Text>
						<Card className="w-full flex flex-col items-start gap-2 bg-gray-100">

							<div className="w-full grid grid-cols-2 justify-between gap-2">
								<Text className="text-gray-600">Montant total</Text>
								<Text className="font-medium text-right">{parseToCurrency(panierPayer.prixTotal)}</Text>
								<Text className="text-gray-600">Produits</Text>
								<Text className="font-medium text-right">{panierPayer.nombreProduits}</Text>
								<Text className="text-gray-600">Date de livraison</Text>
								<Text className="font-medium text-right">{panierPayer.dateLivraison ?? "En attente de planification"}</Text>
								<Text className="text-gray-600">Adresse</Text>
								<Text className="font-medium text-right">{panierPayer.adresseLivraison ?? "En attente de planification"}</Text>
								<Text className="text-gray-600">Référent</Text>
								<Text className="font-medium text-right">{panierPayer.referent?.nom_complet ?? "En attente de planification"}</Text>
							</div>
						</Card>
					</div>
				)
				}

				{/* Bouton de retour */}
				<Button
					onClick={() => navigate('/')}
				>
					Retour à l’accueil
				</Button>
			</Card >
		</div >
	);
}

